'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@workspace/lib';
import { Button, Icon, Modal, Typography } from '@workspace/ui/common';

interface VideoModuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  posterUrl?: string;
  videoName?: string;
}

export function VideoModuleModal({ isOpen, onClose, videoUrl, posterUrl, videoName }: VideoModuleModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const videoFrameStyle = posterUrl
    ? {
        backgroundImage: `linear-gradient(#0003, #0003), url(${JSON.stringify(posterUrl)})`,
      }
    : undefined;

  const closeModal = () => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    setIsPlaying(false);
    setHasStarted(false);
    onClose();
  };

  const playVideo = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      await video.play();
      setIsPlaying(true);
      setHasStarted(true);
    } catch {
      setIsPlaying(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) void playVideo();
  }, [isOpen, playVideo]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      variant="media"
      ariaLabel={videoName || 'Video player'}
      closeButtonAriaLabel="Close video"
      maxWidth="max-w-324"
      className="bg-transparent backdrop:bg-neutral-800/90"
    >
      <div className="my-auto flex w-full flex-col items-center gap-lg px-md pb-md md:px-xl md:pb-xl">
        <div
          className="relative flex aspect-video w-full items-start gap-2xs self-stretch overflow-hidden rounded-2xl bg-secondary-200 bg-cover bg-center"
          style={videoFrameStyle}
        >
          <video
            ref={videoRef}
            src={videoUrl}
            controls={hasStarted}
            playsInline
            preload="metadata"
            onPlay={() => {
              setIsPlaying(true);
              setHasStarted(true);
            }}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            className={cn('size-full object-cover', !hasStarted && 'opacity-0')}
          />
          {!hasStarted && !isPlaying && (
            <Button
              overrideClasses
              aria-label="Play video"
              onClick={() => void playVideo()}
              className="absolute inset-0 flex cursor-pointer items-center justify-center"
            >
              <Icon name="PlayCircleWhite" size={48} className="size-12 md:size-16" />
            </Button>
          )}
        </div>

        {videoName && (
          <Typography variant="bodyXs" className="text-center text-white!">
            {videoName}
          </Typography>
        )}
      </div>
    </Modal>
  );
}
